import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const {FiTool, FiAlertTriangle, FiCheckCircle, FiClock, FiShield, FiHome, FiAward, FiWrench, FiPhone} = FiIcons;

const MaintenanceServicesPage = () => {
  const maintenanceServices = [
    {
      icon: FiAlertTriangle,
      title: '24/7 Emergency Repairs',
      description: 'Round-the-clock emergency response for urgent issues affecting tenant safety or property integrity.',
      features: ['2-hour response time', 'Emergency contractor network', 'Immediate damage assessment', 'Tenant communication']
    },
    {
      icon: FiHome,
      title: 'Preventive Maintenance',
      description: 'Proactive maintenance programs to prevent costly repairs and extend property lifespan.',
      features: ['Seasonal maintenance schedules', 'HVAC system servicing', 'Plumbing inspections', 'Electrical safety checks']
    },
    {
      icon: FiWrench,
      title: 'General Repairs & Upkeep',
      description: 'Comprehensive repair services for all property systems and components.',
      features: ['Appliance repair/replacement', 'Fixture maintenance', 'Painting & touch-ups', 'Flooring repairs']
    },
    {
      icon: FiShield,
      title: 'Property Inspections',
      description: 'Regular detailed inspections to identify maintenance needs and ensure property condition.',
      features: ['Quarterly property inspections', 'Move-in/move-out inspections', 'Photo documentation', 'Condition reports']
    }
  ];

  const benefits = [
    '2-Hour Emergency Response',
    '30% Lower Maintenance Costs',
    'Extended Property Lifespan',
    'Professional Contractor Network',
    'Detailed Service Records',
    'Preventive Maintenance Programs'
  ];

  const contractorNetwork = [
    'Plumbing & Heating Specialists',
    'Electrical Contractors',
    'HVAC Technicians',
    'Appliance Repair Services',
    'General Contractors',
    'Emergency Response Teams'
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Property Maintenance Services Calgary",
    "description": "Professional property maintenance services in Calgary with 24/7 emergency response, preventive maintenance programs, and comprehensive repair solutions.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    },
    "serviceType": ["Property Maintenance", "Emergency Repairs", "Preventive Maintenance", "Property Inspections", "Contractor Management"]
  };

  return (
    <>
      <SEOHead
        title="Property Maintenance Services Calgary | 24/7 Emergency Repairs"
        description="DGM Property Management provides comprehensive maintenance services in Calgary. 24/7 emergency repairs, preventive maintenance, professional contractor network. 2-hour response time. Call 587-800-1215."
        keywords="property maintenance Calgary, emergency repairs Calgary, preventive maintenance Calgary, property repairs Calgary, building maintenance, Calgary maintenance services"
        canonical="/services/maintenance-services"
        ogImage="https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Property Maintenance Services Calgary
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Professional maintenance solutions with 24/7 emergency response and preventive maintenance programs. 
                Protect your investment with expert property care and 2-hour emergency response times.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Maintenance Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Maintenance Solutions</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From emergency repairs to preventive maintenance, we keep your Calgary property in optimal condition.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {maintenanceServices.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                      <div className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contractor Network Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Professional Contractor Network</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our network of licensed, insured, and vetted contractors ensures quality workmanship at competitive prices.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contractorNetwork.map((contractor, index) => (
                <motion.div
                  key={contractor}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-lg p-6 text-center shadow-lg"
                >
                  <SafeIcon icon={FiTool} className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900">{contractor}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8">Why Choose Our Maintenance Services?</h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <SafeIcon icon={FiAward} className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <span className="text-lg text-gray-700">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Professional property maintenance and repair services"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Emergency Response Section */}
        <section className="py-20 bg-red-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <div className="inline-flex items-center gap-3 bg-red-100 text-red-700 px-6 py-3 rounded-full font-semibold">
                  <SafeIcon icon={FiAlertTriangle} className="w-6 h-6" />
                  24/7 Emergency Response Available
                </div>
                <h2 className="text-4xl font-bold text-gray-900">Emergency Maintenance? We're Here 24/7</h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Don't wait for property emergencies. Our team responds within 2 hours to urgent maintenance issues.
                </p>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                  Emergency Hotline: 587-800-1215
                </motion.a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Protect Your Calgary Property Investment</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Our professional maintenance services extend property lifespan and reduce costly repairs. 
                Contact us today for comprehensive property care solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Schedule Maintenance Consultation
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default MaintenanceServicesPage;